import { OrderConfirmationError } from "../../infrastructure/api/order-confirmation-contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { useWorkspaceScope } from "../../../../shared/hooks/useWorkspaceScope";
import { orderConfirmationGateway } from "../../infrastructure/api/order-confirmation-gateway";
import type { OrderConfirmationWorkspace } from "../../domain/order-confirmation";
const silent = () => { };
export function useOrderConfirmationData(token: string) {
  const scope = useWorkspaceScope(token, silent);
  const [state, setState] = useState<{
    scope: typeof scope;
    workspace: OrderConfirmationWorkspace | null;
    loading: boolean;
    error: string;
    retryable: boolean;
  }>({
    scope,
    workspace: null,
    loading: true,
    error: "",
    retryable: true
  });
  const request = useRef({
    scope,
    serial: 0,
    controller: new AbortController(),
    authenticated: false,
    exchange: null as Promise<void> | null
  });
  if (request.current.scope !== scope) {
    request.current.controller.abort(); request.current = {
      scope,
      serial: 0,
      controller: new AbortController(),
      authenticated: false,
      exchange: null
    };
  }
  const context = request.current;
  const invalidate = useCallback(() => {
    if (!scope.isCurrent()) return;
    context.serial++; context.controller.abort();
    setState(previous => previous.scope === scope ? { ...previous, loading: false } : previous);
  }, [context, scope]);
  const load = useCallback(async () => {
    if (!scope.isCurrent()) return;
    const serial = ++context.serial;
    context.controller.abort(); context.controller = new AbortController();
    const current = () => scope.isCurrent() && context.serial === serial;
    setState(previous => ({
      scope,
      workspace: previous.scope === scope ? previous.workspace : null,
      loading: true,
      error: "",
      retryable: true
    }));
    try {
      if (token) {
        context.exchange ??= orderConfirmationGateway.exchange(token);
        await context.exchange;
        if (!current()) return;
        context.authenticated = true;
        history.replaceState({}, "", "/order-access");
      }
      const workspace = await orderConfirmationGateway.current(context.controller.signal);
      if (current()) setState({
        scope,
        workspace,
        loading: false,
        error: "",
        retryable: true
      });
    } catch (caught) {
      if (!current()) return;
      const expired = caught instanceof OrderConfirmationError && caught.code === "ORDER_SESSION_INVALID";
      setState(previous => ({
        scope,
        workspace: !expired && previous.scope === scope ? previous.workspace : null,
        loading: false,
        error: caught instanceof Error ? caught.message : "Sipariş açılamadı.",
        retryable: !expired && (!token || context.authenticated)
      }));
    }
  }, [context, scope, token]);
  useEffect(() => {
    void load();
    return () => { context.serial++; context.controller.abort(); };
  }, [load, context]);
  return {
    scope,
    workspace: state.scope === scope ? state.workspace : null,
    loading: state.scope !== scope || state.loading,
    error: state.scope === scope ? state.error : "",
    canRetryRead: state.scope === scope && state.retryable,
    load,
    invalidate
  };
}
